import { useCallback, useEffect, useMemo, useState } from "react";
import { hvoFirestore, vceFirestore } from "configs/FirebaseConfig";
import {
  FIRESTORE_CAMPAIGN_SENDS_TABLE,
  FIRESTORE_PERSONAL_DISCOUNTS_TABLE,
} from "constants/FirestoreConstant";

const fetchTagged = async (firestore, collectionName, shopSource) => {
  const snapshot = await firestore.collection(collectionName).get();
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data(), shopSource }));
};

/**
 * Loads campaignSends and personalDiscounts from both shops (VCE + HVO) and derives
 * reporting aggregates: sends per segment/mailType/campaign, conversions (resulting orders +
 * revenue) and personal-discount funnel (issued / redeemed / active / expired).
 */
export const useCampaignReports = (options = {}) => {
  const { shopSource = "all" } = options;
  const [sends, setSends] = useState([]);
  const [personalDiscounts, setPersonalDiscounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const shops = [];
        if (shopSource === "all" || shopSource === "VCE") shops.push(["VCE", vceFirestore]);
        if (shopSource === "all" || shopSource === "HVO") shops.push(["HVO", hvoFirestore]);

        const sendPromises = shops.map(([src, fs]) =>
          fetchTagged(fs, FIRESTORE_CAMPAIGN_SENDS_TABLE, src),
        );
        const pdPromises = shops.map(([src, fs]) =>
          fetchTagged(fs, FIRESTORE_PERSONAL_DISCOUNTS_TABLE, src),
        );

        const [sendResults, pdResults] = await Promise.all([
          Promise.all(sendPromises),
          Promise.all(pdPromises),
        ]);

        if (cancelled) return;
        setSends(sendResults.flat());
        setPersonalDiscounts(pdResults.flat());
      } catch (err) {
        if (cancelled) return;
        console.error("Error loading campaign reports:", err);
        setError(err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [shopSource, reloadKey]);

  const stats = useMemo(() => {
    const sent = sends.filter((s) => s.status === "sent");
    const failed = sends.filter((s) => s.status === "failed");
    const skipped = sends.filter((s) => s.status === "skipped_unsubscribed");

    const withOrder = sent.filter((s) => !!s.resultingOrderId);
    const revenue = withOrder.reduce((sum, s) => sum + (s.resultingRevenue || 0), 0);
    const opened = sent.filter((s) => !!s.openedAt);
    const clicked = sent.filter((s) => !!s.clickedAt);

    // Group sends by a key, returning sent/orders/revenue/opens/clicks per group
    const groupBy = (keyFn) => {
      const map = {};
      sent.forEach((s) => {
        const key = keyFn(s) || "—";
        if (!map[key]) map[key] = { key, sent: 0, orders: 0, revenue: 0, opened: 0, clicked: 0 };
        map[key].sent += 1;
        if (s.resultingOrderId) {
          map[key].orders += 1;
          map[key].revenue += s.resultingRevenue || 0;
        }
        if (s.openedAt) map[key].opened += 1;
        if (s.clickedAt) map[key].clicked += 1;
      });
      return Object.values(map)
        .map((g) => ({
          ...g,
          conversionRate: g.sent > 0 ? (g.orders / g.sent) * 100 : 0,
          openRate: g.sent > 0 ? (g.opened / g.sent) * 100 : 0,
          clickRate: g.sent > 0 ? (g.clicked / g.sent) * 100 : 0,
        }))
        .sort((a, b) => b.sent - a.sent);
    };

    const bySegment = groupBy((s) => s.segment);
    const byMailType = groupBy((s) => s.mailType);
    const byCampaign = groupBy((s) => s.campaignName);

    const pdIssued = personalDiscounts.length;
    const pdRedeemed = personalDiscounts.filter((p) => p.status === "redeemed").length;
    const pdActive = personalDiscounts.filter((p) => p.status === "active").length;
    const pdExpired = personalDiscounts.filter((p) => p.status === "expired").length;
    const pdRedemptionRate = pdIssued > 0 ? (pdRedeemed / pdIssued) * 100 : 0;

    return {
      totalSent: sent.length,
      totalFailed: failed.length,
      totalSkipped: skipped.length,
      totalOrders: withOrder.length,
      totalRevenue: revenue,
      totalOpened: opened.length,
      totalClicked: clicked.length,
      conversionRate: sent.length > 0 ? (withOrder.length / sent.length) * 100 : 0,
      openRate: sent.length > 0 ? (opened.length / sent.length) * 100 : 0,
      clickRate: sent.length > 0 ? (clicked.length / sent.length) * 100 : 0,
      bySegment,
      byMailType,
      byCampaign,
      personalDiscounts: {
        issued: pdIssued,
        redeemed: pdRedeemed,
        active: pdActive,
        expired: pdExpired,
        redemptionRate: pdRedemptionRate,
      },
    };
  }, [sends, personalDiscounts]);

  return { sends, personalDiscounts, stats, isLoading, error, refresh };
};
